// File: components/layouts/MainLayout.jsx
// ---------------------------------------------------------------

import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import HamburgerMenu from '../../Components/HamburgerMenu/HamburgerMenu.jsx';
import UpperPanelDetails from '../../Components/Widgets/UpperPanelDetails/UpperPanelDetails.jsx';
import CustomBreadcrumbs from '../../Components/CustomBreadcrumbs/CustomBreadcrumbs.jsx';
import NotificationModal from '../../Components/Notifications/NotificationModal.jsx';

import { SignalRProvider } from '../../Contexts/signalR.js';
import { CasePresenceProvider } from '../../Contexts/CasePresenceContext.js';
import ConcurrentViewersBanner from '../../Components/Widgets/ConcurrentViewersBanner/ConcurrentViewersBanner.jsx';
import { FileProvider } from '../../Contexts/FileContext.js';
import { ChatProvider } from '../../Contexts/ChatProvider.js';
import { ClientProvider } from '../../Contexts/ClientContext';
import { OcrJobsProvider } from '../../Contexts/OcrJobsContext';
import { ReportingPeriodProvider, useReportingPeriod } from '../../Contexts/ReportingPeriodContext';
import OcrJobProgress from '../../Components/OcrJobProgress/OcrJobProgress.jsx';
import UnprocessedItems from '../../Components/UnprocessedItems';
import { useHamburgerMenu } from '../../Contexts/HamburgerMenuContext.js';
import { PermissionsContext } from '../../Contexts/Permissions';
import { useMouseActivity } from '../../hooks/useMouseActivity';
import { useIdleTimeout } from '../../hooks/useIdleTimeout';
import { NotificationCenterProvider } from '../../Contexts/NotificationCenter';


import { useDirectionByLanguage } from '../../hooks/useDirectionByLanguage';
import { useClientNavigation } from '../../hooks/useClientNavigation';
import { useCheckTokenAndPaymentStatus } from '../../hooks/checkTokenAndPaymentStatus.js';
import useNotificationModal from '../../hooks/useNotificationModal.js';


import { clearSelectedClient } from '../../store/reducers/selectedClientSlice';
import AuthService from '../../Services/auth.service.js';
import Constants from '../../assests/Constants/constants';

import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box, Typography, LinearProgress } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentDialog from '../../Components/payments/PaymentDialog.jsx';
import TrialDialog from '../../Components/payments/TrialDialog.jsx';
import PaymentService from '../../Services/payment.service.js';
import XtramCollectionsAccountantService from '../../Services/XtramCollectionsAccountant.service.js';
import { useTrialStatus } from '../../hooks/useTrialStatus.js';
import ChatbotWidget from '../../Chatbot/ChatbotWidget.jsx';
import HelpIcon from '../../Components/Widgets/HelpIcon/HelpIcon.js';
import UserFeedbackPromptHost, { UserFeedbackManualListener } from '../../Components/Widgets/UserFeedbackModal/UserFeedbackPromptHost';
import { BACK_FUNCTION_KEY } from '../../Utils/getScreenFunctionKey.js';
import './main.css';
/* ────────────────────────────────────────────────────────────────── */
const MAIN_PATHS = [
  '/', '/main', '/trackinglist', '/assignments', '/accountant-details',
  '/subaccountants', '/addclient', '/user-activities', '/creditterminal', '/finsite'
];


const BREADCRUMB_ROUTES = [
  '/accountcardstable', '/accountcarddashboard', '/ledgerreport',
  '/profitandlossreport', '/otherdocument', '/submissions', '/document',
  '/paymentacceptance', '/banktransactions', '/cardmatching', '/bankpages',
  '/collectionpage', '/transactiondocuments', '/addclient', '/createinvoice', '/createlawyerinvoice',
  '/receipttax', '/sortcodereport', '/assets', '/journalentry', '/clientitems',
  '/subaccountants', '/holdingitems', '/assignments', '/trackinglist', '/user-activities', '/inventorymanagement',
  '/vehiclemanagement', '/garageworkdiary', '/createselfinvoice', '/signaturedocument', '/forms', '/auditreports', '/profittandloss6111',
  '/advancecalculator', '/annualreportquestionnaire', '/receivables', '/receivablesaging', '/grantsstatus', '/projects', '/createfixedpurchasesale', '/audit',
  '/creditterminal', '/finsite', '/cashflowforecast',
  '/report1214', '/report1214b', '/report1281', '/foreigntravelreport', '/clientexpenseassignmentreport',
];
/* ────────────────────────────────────────────────────────────────── */

/* ────────────────────────────────────────────────────────────────── */

function LayoutToolbarRow({
  direction,
  isBreadcrumbPage,
  hideBackBtn,
  client,
  token,
  goBack,
  t,
}) {
  const {
    date,
    startDate,
    endDate,
    biStartDate,
    biEndDate,
    isRange,
    isBiMonthly,
  } = useReportingPeriod();

  const unprocessedDateProps = isRange
    ? { selectedDate: startDate, startDate, endDate, isRange: true }
    : isBiMonthly
      ? { selectedDate: biStartDate, startDate: biStartDate, endDate: biEndDate, isRange: true }
      : { selectedDate: date, isRange: false };

  const showUnprocessed = Boolean(client?.caseId && token);
  const hasLeadingContent = isBreadcrumbPage || showUnprocessed;

  return (
    <div
      className={`layout-toolbar-row ${direction || ''}`}
      dir={direction}
    >
      {hasLeadingContent && (
        <div className="layout-toolbar-leading">
          {isBreadcrumbPage && <CustomBreadcrumbs direction={direction} />}
          {showUnprocessed && (
            <UnprocessedItems client={client} token={token} {...unprocessedDateProps} />
          )}
        </div>
      )}
      {!hideBackBtn && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            flexWrap: 'wrap',
            marginInlineStart: hasLeadingContent ? 'auto' : undefined,
          }}
        >
          <div
            className="backIconBackground backIconBackground--labeled"
            onClick={goBack}
            title={`${t('Back')} (${BACK_FUNCTION_KEY})`}
          >
            <span className="fn-key-corner-badge">{BACK_FUNCTION_KEY}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 32 32"
              fill="none"
              aria-hidden="true"
              style={direction === 'rtl' ? { transform: 'scaleX(-1)' } : undefined}
            >
              <path
                d="M20 21.1667L14.8267 15.9933L20 10.82C20.52 10.3 20.52 9.45999 20 8.93999C19.48 8.41999 18.64 8.41999 18.12 8.93999L12 15.06C11.48 15.58 11.48 16.42 12 16.94L18.12 23.06C18.64 23.58 19.48 23.58 20 23.06C20.5067 22.54 20.52 21.6867 20 21.1667Z"
                fill="black"
                fillOpacity="0.9"
              />
            </svg>
            <span className="back-label">{t('Back')}</span>
          </div>
        </div>
      )}
    </div>
  );
}

const MainLayout = () => {
  /* redux + contexts ------------------------------------------------- */
  const client = useSelector(s => s.selectedClientSlice);
  const dispatch = useDispatch();
  const { menuProps, setMenuProps } = useHamburgerMenu();
  const { permissionsState, user, userId, userTypeContext, token, setActiveCase, resetPermissionsState } = useContext(PermissionsContext);

  const { t, i18n } = useTranslation();
  const direction = useDirectionByLanguage(i18n);
  const location = useLocation();
  const navigate = useNavigate();
  const { navigateToPrevious } = useClientNavigation(client);
  const { isPaid, checked } = useCheckTokenAndPaymentStatus();
  const userTypeStr = userTypeContext?.toString() || '';
  const isPremium = permissionsState?.Premium ?? false;

  /* routes that should **add the writing direction** (`${direction}`)   */
  const DIRECTION_ROUTES = [
    '/client-details',
    '/document',
    '/journalentry',
    '/addclient',
    '/main',
    '/assets',
    '/accountcardstable',
    '/ledgerreport',
    '/profitandlossreport',
    '/assignments',
    '/banktransactions',
    '/cardmatching',
    '/bankpages',
    '/collectionpage',
    '/transactiondocuments',
    '/otherdocument',
    '/accountcarddashboard',
    '/clientexpenseassignmentreport',
  ];

  /* payment dialog state -------------------------------------------- */
  const [dialogOpen, setDialogOpen] = useState(false);
  const [amount, setAmount] = useState(null);     // VAT-inclusive preview
  const [yearly, setYearly] = useState(false);   // mutable only for client
  const userType = userTypeContext?.toString();                      // numeric (Constants.UserType.*)
  const isClient = userType === Constants.UserType.Client;
  const isAcc = userType === Constants.UserType.Accountant;
  const isSubAcc = userType === Constants.UserType.SubAccountant;


  /* paths helpers ---------------------------------------------------- */
  const path = location.pathname.toLowerCase();
  const isMainPage = MAIN_PATHS.includes(path);
  const isAssignmentsPage = path.startsWith('/assignments');
  const isClientDetails = path.startsWith('/client-details');
  const isDirectionPage = DIRECTION_ROUTES.some(r => path.startsWith(r));
  const isBreadcrumbPage = BREADCRUMB_ROUTES.some(r => path.startsWith(r));
  const isSignaturePage = path.startsWith('/sign-document') || path.startsWith('/signature-success');

  const pageClass = isAssignmentsPage 
    ? 'template-page-withoutHeight' 
    : isSignaturePage 
    ? 'template-page signature-page'
    : 'template-page';
  const containerClass = isDirectionPage ? `template-container ${direction}` : 'template-container';

  const { needsTrial, loading: trialLoading, status: payStat, error: payErr, cancel: cancelPay } =
    useTrialStatus(isAcc, !isSubAcc && checked);
  const [showTrial, setShowTrial] = useState(false);

  /* ── Anti-tampering: continuous payment verification ────────────── */
  useEffect(() => {
    if (!dialogOpen || isPaid || !checked) return;
    
    const verifyInterval = setInterval(() => {
      // If payment is required but dialog somehow closed, force reload
      if (!isPaid && checked && !dialogOpen) {
        window.location.reload();
      }
    }, 3000); // Check every 3 seconds

    return () => clearInterval(verifyInterval);
  }, [dialogOpen, isPaid, checked]);

  /* ──────────────────────────────────────────────────────────────── */
  /*  Notifications                                                  */
  /* ──────────────────────────────────────────────────────────────── */


  const getUserState = useCallback(() => ({ userId, userType }), [userId, userType]);


  const notifFeed = useNotificationModal(getUserState);

  const openNotifications = useCallback(() => {
    notifFeed.refresh?.(); // whichever the hook exposes
    notifFeed.open();
  }, [notifFeed]);
  const notifCtxValue = useMemo(() => ({
    openNotifications,
    unreadCount: notifFeed.unreadCount,
    notifVisible: notifFeed.visible,
  }), [notifFeed.open, notifFeed.unreadCount, notifFeed.visible]);

  useEffect(() => {
    if (needsTrial && !isSubAcc) setShowTrial(true);         // open exactly once
  }, [needsTrial]);
  /* ─────── Fetch preview amount (server returns VAT-included) ────── */
  const fetchPreview = async (id, wantYearly = yearly) => {

    if (!id) return;


    const amt = isAcc
      ? await PaymentService.accountant.preview(id, false)
      : await PaymentService.client.preview(id, wantYearly);

    setAmount(amt);
    setDialogOpen(true);
  };

  /* when auth-status finished & unpaid – force dialog --------------- */
  /* For client login use user?.caseId so we have a valid id even before redux client is set */
  useEffect(() => {
    if (isSubAcc) return;
    if (!checked || isPaid) return;
    if (!(isClient || isAcc)) return;          // admins / subs – skip

    const id = isClient ? (user?.caseId ?? client?.caseId) : user?.accountantId;
    if (id) fetchPreview(id);
  }, [checked, isPaid, client?.caseId, user?.caseId, user?.accountantId, isClient, isAcc]);

  /* When client is blocked and preview returned 0/null, fetch unpaid from monthly breakdown so dialog shows amount.
   * If first call returns 0 and empty monthlyBalances (e.g. client pays from month 3, we're in month 2), retry with end of next month. */
  useEffect(() => {
    if (!dialogOpen || !isClient || !user?.caseId || !token) return;
    const amt = amount != null && amount > 0 ? amount : null;
    if (amt !== null) return;

    let cancelled = false;
    const getFallback = (res) => {
      if (!res?.success || !res?.data) return 0;
      const unpaid = Number(res.data.unpaidBalance);
      const fromMonths = (res.data.monthlyBalances || [])
        .filter((m) => !m.isPaid)
        .reduce((s, m) => s + (Number(m.balance) || 0), 0);
      return unpaid > 0 ? unpaid : fromMonths;
    };
    const isEmpty = (data) =>
      (data.unpaidBalance == null || Number(data.unpaidBalance) === 0) &&
      (!data.monthlyBalances || data.monthlyBalances.length === 0);

    XtramCollectionsAccountantService.getClientMonthlyBreakdown(user.caseId, token)
      .then(async (res) => {
        if (cancelled) return;
        let fallback = getFallback(res);
        if (fallback > 0) {
          setAmount(fallback);
          return;
        }
        if (res?.success && res?.data && isEmpty(res.data)) {
          const now = new Date();
          const endOfNextMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 2, 0));
          const retry = await XtramCollectionsAccountantService.getClientMonthlyBreakdown(
            user.caseId,
            token,
            null,
            endOfNextMonth
          );
          if (!cancelled && retry?.success && retry?.data) {
            fallback = getFallback(retry);
            if (fallback > 0) setAmount(fallback);
          }
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dialogOpen, isClient, user?.caseId, token, amount]);
  /* reset redux when landing on a “home” route ---------------------- */
  useEffect(() => {
    if (isMainPage) {
      if (client) dispatch(clearSelectedClient());
      setMenuProps({});
    }
  }, [isMainPage, client, dispatch, setMenuProps]);

  useEffect(() => {
    setActiveCase(client?.caseId ?? null);   // null = no client selected
  }, [client?.caseId]);

  /* ui helpers ------------------------------------------------------- */
  const hideBackBtn =
    isClientDetails &&
    userType === Constants.UserType.Client &&
    !(permissionsState?.Premium ?? false);

  /* ------------------------------------------------------------------ */

  const handleGoBack = () => {
    navigateToPrevious(location);
  };

  const hamburgerProps = isMainPage ? {} : menuProps;
  const showToolbar = (!isMainPage && client) || isBreadcrumbPage;

  // ── global mouse-activity tracking (fires max once every 2 min) ──∑
  useMouseActivity({
    userId,
    userType,
    caseId: client?.caseId ?? null,
    token: token,
  });

  // ── auto-logout after 5 min of inactivity (with 30-sec warning) ──
  const handleIdleLogout = useCallback(async () => {
    try {
      resetPermissionsState?.();
      await AuthService.logout();
      navigate('/login', { replace: true });
    } catch {
      navigate('/login', { replace: true });
    }
  }, [navigate, resetPermissionsState]);

  const { showWarning: showIdleWarning, secondsLeft, dismissWarning } = useIdleTimeout({
    onIdle: handleIdleLogout,
    idleMs: 15 * 60 * 1000,
    warningMs: 30 * 1000,
    enabled: !!userId,
  });

  const goBack = () => navigateToPrevious(location);

  const onPaidOk = () => {
    setDialogOpen(false);
    window.location.reload();
  };

  /* plan selector (only clients) updates both local & preview */
  const handlePlanToggle = async wantYearly => {
    setYearly(wantYearly);

    const id = client?.caseId;
    if (isClient && id) await fetchPreview(id, wantYearly);
  };

  const handleTrialSuccess = () => {
    setShowTrial(false);          // closes modal
    window.location.reload();
  };
  /* blur when any modal is open */
  const blurActive = dialogOpen || notifFeed.visible;
  /* ================================================================= */
  /*  RENDER                                                          */
  /* ================================================================= */
  return (
    <ReportingPeriodProvider>
    <ChatProvider>
      <SignalRProvider>
        <CasePresenceProvider>
        <FileProvider>
          <NotificationCenterProvider value={notifCtxValue}>
            <div className={pageClass}>
              <div className={containerClass}
                style={dialogOpen ? { filter: 'blur(3px)', pointerEvents: 'none' } : {}}>
                {/* Above the rest of the chrome so it reads as a top-of-screen warning on every
                    client screen; self-hides when no client is open or nobody else is in. */}
                <ConcurrentViewersBanner />
                <ClientProvider client={client}>
                  <OcrJobsProvider token={token} caseId={client?.caseId} accountantId={user?.accountantId}>
                  <div className={isSignaturePage ? 'hide-on-mobile' : ''}>
                    <HamburgerMenu {...(isMainPage ? {} : menuProps)} payStat={payStat} payLoad={trialLoading} payErr={payErr} cancelPay={cancelPay} />

                    {isMainPage
                      ? <UpperPanelDetails />
                      : <UpperPanelDetails client={client} />}
                  

                  {showToolbar && (
                    <LayoutToolbarRow
                      direction={direction}
                      isBreadcrumbPage={isBreadcrumbPage}
                      hideBackBtn={hideBackBtn}
                      client={client}
                      token={token}
                      goBack={goBack}
                      t={t}
                    />
                  )}
                  </div>

                  <div className="main-content">
                    {/* Block rendering if payment required - prevents DevTools bypass */}
                    {(isPaid || !checked || isSubAcc) ? <Outlet /> : null}
                  </div>
                  <OcrJobProgress />
                  </OcrJobsProvider>
                </ClientProvider>
              </div>
            </div>

            <ChatbotWidget />
            <HelpIcon />
            <UserFeedbackPromptHost />
            <UserFeedbackManualListener />

            <NotificationModal
              visible={notifFeed.visible}
              onClose={notifFeed.close}
              notifications={notifFeed.notifications}
              loading={notifFeed.loading}
              markLocalRead={notifFeed.markLocalRead}
              getUserState={getUserState}
            />
            <TrialDialog
              open={showTrial && !trialLoading}
              isAccountant={isAcc}
              onSuccess={handleTrialSuccess}
            />

            {/* ---------- Blocking payment dialog (cannot close) ---------- */}
            <PaymentDialog
              open={dialogOpen}
              onClose={() => { }}
              amount={amount}
              yearly={yearly}
              allowYearly={isClient}
              userId={userId}
              userType={userTypeContext}
              onSuccess={onPaidOk}
              onPlanChange={handlePlanToggle}
            />

            {/* ---------- Idle-timeout warning dialog ---------------------- */}
            <Dialog
              open={showIdleWarning}
              onClose={dismissWarning}
              maxWidth="xs"
              fullWidth
              PaperProps={{
                sx: { borderRadius: 3, overflow: 'hidden' },
              }}
            >
              <Box sx={{
                background: 'linear-gradient(135deg, #ff9800 0%, #f44336 100%)',
                py: 2,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: 1,
              }}>
                <AccessTimeIcon sx={{ color: '#fff', fontSize: 32 }} />
                <Typography variant="h6" sx={{ color: '#fff', fontWeight: 700 }}>
                  {t('SessionTimeout')}
                </Typography>
              </Box>

              <DialogContent sx={{ textAlign: 'center', pt: 3, pb: 1 }}>
                <Typography
                  variant="h2"
                  sx={{ fontWeight: 800, color: '#f44336', mb: 1 }}
                >
                  {secondsLeft}
                </Typography>

                <LinearProgress
                  variant="determinate"
                  value={(secondsLeft / 30) * 100}
                  sx={{
                    height: 6,
                    borderRadius: 3,
                    mb: 2,
                    backgroundColor: '#ffcdd2',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: secondsLeft <= 10 ? '#f44336' : '#ff9800',
                      transition: 'transform 1s linear',
                    },
                  }}
                />

                <DialogContentText sx={{ fontSize: '1rem', color: 'text.secondary' }}>
                  {t('SessionTimeoutMessage', { seconds: secondsLeft })}
                </DialogContentText>
              </DialogContent>

              <DialogActions sx={{ justifyContent: 'center', pb: 3, gap: 2 }}>
                <Button
                  onClick={handleIdleLogout}
                  variant="outlined"
                  color="error"
                  sx={{ borderRadius: 20, px: 3, textTransform: 'none', fontWeight: 600 }}
                >
                  {t('LogOut')}
                </Button>
                <Button
                  onClick={dismissWarning}
                  variant="contained"
                  sx={{
                    borderRadius: 20,
                    px: 4,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)',
                    },
                  }}
                >
                  {t('StayConnected')}
                </Button>
              </DialogActions>
            </Dialog>
          </NotificationCenterProvider>
        </FileProvider>
        </CasePresenceProvider>
      </SignalRProvider>
    </ChatProvider>
    </ReportingPeriodProvider>
  );
};

export default MainLayout;